import { Hash, CreditCard, Clock } from 'lucide-react';
import React from 'react';
import DataTable from 'react-data-table-component';
import { useQuery } from '@apollo/client';
import { GET_ALL_ORDERS } from '../apollo/Queries';

const customStyles = {
    header: {
        style: {
            minHeight: '56px',
        },
    },
    headRow: {
        style: {
            backgroundColor: '#0D3B3F',
            borderTopStyle: 'solid',
            borderTopWidth: '1px',
            borderTopColor: 'rgba(255,255,255,0.1)',
            borderRadius: '1.5rem 1.5rem 0 0',
            marginBottom: '8px',
        },
    },
    headCells: {
        style: {
            color: '#EAB308',
            fontSize: '10px',
            fontWeight: '900',
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            paddingLeft: '24px',
        },
    },
    cells: {
        style: {
            fontSize: '13px',
            color: '#0D3B3F',
            paddingLeft: '24px',
            fontWeight: '600',
        },
    },
    rows: {
        style: {
            minHeight: '72px',
            borderRadius: '1rem',
            backgroundColor: 'white',
            marginBottom: '8px',
            border: '1px solid #F3F4F6',
            '&:hover': {
                backgroundColor: '#F9FAFB',
                transform: 'scale-[1.01]',
                boxShadow: '0 10px 30px rgba(0,0,0,0.02)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }
        },
    },
    pagination: {
        style: {
            border: 'none',
            fontSize: '11px',
            fontWeight: '800',
            color: '#0D3B3F',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginTop: '12px',
        },
    },
};

const columns = [
    {
        name: 'Order Number',
        selector: row => row.orderNo,
        sortable: true,
        cell: row => (
            <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-premium-teal/5 flex items-center justify-center text-premium-teal">
                    <Hash size={14} />
                </div>
                <span className="font-black tracking-tighter text-premium-teal">{row.orderNo}</span>
            </div>
        )
    },
    {
        name: 'Amount',
        selector: row => {
            const total = row.orderItems?.reduce((acc, item) => {
                const price = parseFloat(item.price || 0);
                const quantity = parseInt(item.quantity || 0);
                return acc + (price * quantity);
            }, 0);
            return total;
        },
        cell: row => {
            const total = row.orderItems?.reduce((acc, item) => {
                const price = parseFloat(item.price || 0);
                const quantity = parseInt(item.quantity || 0);
                return acc + (price * quantity);
            }, 0);
            return (
                <div className="flex items-center gap-2">
                    <CreditCard size={14} className="text-premium-gold/50" />
                    <span className="font-mono text-sm font-black text-premium-teal">${total ? total.toLocaleString() : "0.00"}</span>
                </div>
            );
        }
    },
    {
        name: 'Date',
        selector: row => row.createdAt,
        cell: row => (
            <div className="flex items-center gap-2 text-gray-400">
                <Clock size={14} />
                <span className="text-[10px] font-bold uppercase tracking-widest">{row.createdAt ? row.createdAt.substring(0, 10) : "N/A"}</span>
            </div>
        )
    },
    {
        name: 'Customer',
        selector: row => row.orderedBy?.firstName,
        cell: row => (
            <div className="flex flex-col">
                <span className="font-black text-premium-teal tracking-tight">{row.orderedBy?.firstName} {row.orderedBy?.lastName}</span>
                <span className="text-[9px] text-gray-400 font-bold uppercase tracking-[0.2em]">{row.orderedBy?.phone}</span>
            </div>
        )
    },
    {
        name: 'Status',
        cell: row => {
            let colorClass = '';
            switch (row.status) {
                case 'PENDING': colorClass = 'bg-orange-50 text-orange-600 border-orange-100'; break;
                case 'CANCELLED': colorClass = 'bg-red-50 text-red-600 border-red-100'; break;
                case 'ACCEPTED': colorClass = 'bg-[#EAB308]/10 text-[#EAB308] border-[#EAB308]/20'; break;
                case 'PROCESSED': colorClass = 'bg-blue-50 text-blue-600 border-blue-100'; break;
                case 'DELIVERED': colorClass = 'bg-green-50 text-green-600 border-green-100'; break;
                case 'ON_DELIVERY': colorClass = 'bg-premium-teal/10 text-premium-teal border-premium-teal/20'; break;
                default: colorClass = 'bg-gray-50 text-gray-600 border-gray-100';
            }
            return <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${colorClass}`}>{row.status}</span>;
        },
    },
];

export default function RecentOrdersTable({ limit }) {
    const { data, loading } = useQuery(GET_ALL_ORDERS, {
        variables: {
            page: 0,
            size: 10
        }
    });

    const orders = data?.allOrders?.content;
    const displayedData = limit ? (Array.isArray(orders) ? orders.slice(0, limit) : []) : (orders || []);

    if (loading) return (
        <div className="flex flex-col justify-center items-center h-48 gap-4 py-10">
            <div className="w-10 h-10 rounded-xl border-2 border-premium-gold/20 border-t-premium-gold animate-spin"></div>
            <p className="text-[9px] font-black text-premium-gold uppercase tracking-[0.5em] animate-pulse">Updating Feed...</p>
        </div>
    );

    if (!displayedData || displayedData.length === 0) return (
        <div className="text-center py-20 bg-gray-50/50 rounded-[2rem] border border-dashed border-gray-100">
            <p className="text-gray-400 text-xs font-light italic tracking-wide">No orders found in this section.</p>
        </div>
    );

    return (
        <div className="animate-fade-in-up">
            <DataTable
                columns={columns}
                data={displayedData}
                pagination
                customStyles={customStyles}
                noHeader
            />
        </div>
    );
}
